import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { ShieldCheck, ArrowLeft } from "lucide-react";
import { API, LOGO_URL } from "@/config";
import { setAuthToken } from "@/utils/apiClient";

const RESEND_SECONDS = 30;
const OTP_LENGTH = 6;

const OtpVerifyPage = ({ onAuthSuccess }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state || {};
  const { phone, name, purpose } = state;

  const [digits, setDigits] = useState(Array(OTP_LENGTH).fill(""));
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendIn, setResendIn] = useState(RESEND_SECONDS);
  const inputRefs = useRef([]);

  useEffect(() => {
    if (!phone) {
      navigate("/login");
      return;
    }
    // Auto-focus first input
    inputRefs.current[0]?.focus();
  }, [phone, navigate]);

  // Resend countdown
  useEffect(() => {
    if (resendIn <= 0) return;
    const t = setTimeout(() => setResendIn((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [resendIn]);

  const handleChange = (index, value) => {
    const v = value.replace(/\D/g, "").slice(-1);
    const next = [...digits];
    next[index] = v;
    setDigits(next);
    if (v && index < OTP_LENGTH - 1) inputRefs.current[index + 1]?.focus();
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !digits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = (e.clipboardData.getData("text") || "").replace(/\D/g, "").slice(0, OTP_LENGTH);
    if (!pasted) return;
    const next = Array(OTP_LENGTH).fill("");
    for (let i = 0; i < pasted.length; i++) next[i] = pasted[i];
    setDigits(next);
    const focusIdx = Math.min(pasted.length, OTP_LENGTH - 1);
    inputRefs.current[focusIdx]?.focus();
  };

  const handleVerify = async (e) => {
    e?.preventDefault();
    setError(""); setInfo("");
    const otp = digits.join("");
    if (otp.length !== OTP_LENGTH) {
      setError(`Please enter all ${OTP_LENGTH} digits`); return;
    }
    setVerifying(true);
    try {
      const { data } = await axios.post(`${API}/auth/verify-otp`, { phone, otp, name }, { withCredentials: true });
      if (data.access_token) setAuthToken(data.access_token);
      // Hand back to AuthContext-provided callback (sets user state, then we redirect)
      if (onAuthSuccess) onAuthSuccess(data.user);
      navigate("/");
    } catch (err) {
      setError(err.response?.data?.detail || err.message || "Verification failed. Please try again.");
    } finally {
      setVerifying(false);
    }
  };

  const handleResend = async () => {
    if (resendIn > 0 || resending) return;
    setError(""); setInfo("");
    setResending(true);
    try {
      await axios.post(`${API}/auth/send-otp`, { phone, name, purpose });
      setInfo("A new OTP has been sent to your WhatsApp.");
      setResendIn(RESEND_SECONDS);
      setDigits(Array(OTP_LENGTH).fill(""));
      inputRefs.current[0]?.focus();
    } catch (err) {
      setError(err.response?.data?.detail || "Could not resend OTP.");
    } finally {
      setResending(false);
    }
  };

  const maskedPhone = phone ? phone.slice(0, -4).replace(/\d/g, "*").slice(-6) + phone.slice(-4) : "";

  return (
    <div className="finance-app min-h-screen flex items-center justify-center p-4" data-testid="otp-verify-page">
      <div className="finance-card w-full max-w-md">
        <button onClick={() => navigate(purpose === "register" ? "/register" : "/login")} className="flex items-center gap-1 text-gray-500 hover:text-gray-700 text-sm mb-4" data-testid="otp-back">
          <ArrowLeft size={16} /> Back
        </button>
        <div className="text-center mb-6">
          <img src={LOGO_URL} alt="SubiWealth" className="h-14 mx-auto mb-4" />
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-teal-50 text-teal-600 mb-3">
            <ShieldCheck size={28} />
          </div>
          <h1 className="text-2xl font-bold text-gray-800">Verify OTP</h1>
          <p className="text-gray-500 mt-2 text-sm">
            Enter the 6-digit code sent to <span className="font-medium text-gray-700">{phone}</span>
          </p>
        </div>

        {error && (
          <div className="bg-red-50 text-red-600 px-4 py-3 rounded-lg mb-4 text-sm" data-testid="otp-error">{error}</div>
        )}
        {info && (
          <div className="bg-green-50 text-green-700 px-4 py-3 rounded-lg mb-4 text-sm" data-testid="otp-info">{info}</div>
        )}

        <form onSubmit={handleVerify} className="space-y-6">
          <div className="flex gap-2 justify-center" onPaste={handlePaste}>
            {digits.map((d, i) => (
              <input
                key={i}
                ref={(el) => (inputRefs.current[i] = el)}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={d}
                onChange={(e) => handleChange(i, e.target.value)}
                onKeyDown={(e) => handleKeyDown(i, e)}
                className="w-12 h-14 text-center text-xl font-semibold rounded-lg bg-gray-50 border border-gray-200 focus:border-teal-400 focus:ring-2 focus:ring-teal-100 focus:outline-none"
                data-testid={`otp-digit-${i}`}
              />
            ))}
          </div>

          <button type="submit" data-testid="otp-verify-btn" className="w-full nav-btn primary py-3" disabled={verifying}>
            {verifying ? "Verifying..." : "Verify OTP"}
          </button>
        </form>

        <div className="text-center mt-6 text-sm">
          {resendIn > 0 ? (
            <p className="text-gray-500" data-testid="otp-resend-timer">Resend OTP in {resendIn}s</p>
          ) : (
            <button onClick={handleResend} className="text-teal-600 font-medium hover:underline" disabled={resending} data-testid="otp-resend-btn">
              {resending ? "Sending..." : "Resend OTP"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default OtpVerifyPage;
